import React, { useMemo, useCallback } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  addEdge,
} from 'reactflow';
import { Clock, CheckCircle, Package, Truck, AlertCircle, PlayCircle, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';

// Custom timeline node component
const TimelineNode = ({ data }) => {
  const {
    label,
    timestamp,
    status,
    changes,
    isFirst,
    isLast,
    fulfillmentPercentage,
    icon: IconComponent,
    changeType
  } = data;

  const getNodeStyles = () => {
    switch (changeType) {
      case 'created':
        return {
          border: '2px solid #3b82f6',
          background: 'linear-gradient(135deg, #dbeafe 0%, #93c5fd 100%)',
          color: '#1e40af'
        };
      case 'status_change':
        return {
          border: '2px solid #fed402',
          background: 'linear-gradient(135deg, #fef3c7 0%, #fed402 100%)',
          color: '#92400e'
        };
      case 'fulfillment_update':
        return {
          border: '2px solid #10b981',
          background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)',
          color: '#065f46'
        };
      case 'completed':
        return {
          border: '2px solid #059669',
          background: 'linear-gradient(135deg, #ecfdf5 0%, #10b981 100%)',
          color: '#065f46'
        };
      default:
        return {
          border: '2px solid #6b7280',
          background: 'linear-gradient(135deg, #f9fafb 0%, #e5e7eb 100%)',
          color: '#374151'
        };
    }
  };

  return (
    <div className="relative">
      {!isFirst && (
        <Handle type="target" position={Position.Top} className="opacity-0" />
      )}
      
      <div
        style={getNodeStyles()}
        className="px-4 py-3 rounded-lg shadow-lg min-w-[280px] max-w-[320px] transition-all duration-300 hover:shadow-xl"
      >
        <div className="flex items-start space-x-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/30 flex-shrink-0">
            <IconComponent className="h-5 w-5" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-sm truncate">{label}</h3>
              <span className="text-xs opacity-70 ml-2">
                {format(new Date(timestamp), 'MMM dd, HH:mm')}
              </span>
            </div>
            
            {status && (
              <p className="text-xs opacity-80 mb-2">
                Status: <span className="font-medium">{status}</span>
              </p>
            )}
            
            {changes && changes.length > 0 && (
              <div className="space-y-1">
                {changes.map((change, index) => (
                  <div key={index} className="text-xs opacity-80 flex items-center">
                    <ArrowRight className="h-3 w-3 mr-1 flex-shrink-0" />
                    <span className="truncate">{change}</span>
                  </div>
                ))}
              </div>
            )}
            
            {fulfillmentPercentage !== null && fulfillmentPercentage !== undefined && (
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1">
                  <span>Fulfillment</span>
                  <span className="font-medium">{fulfillmentPercentage}%</span>
                </div>
                <div className="w-full bg-white/30 rounded-full h-1.5">
                  <div
                    className="bg-current h-1.5 rounded-full transition-all duration-500"
                    style={{ width: `${fulfillmentPercentage}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {!isLast && (
        <Handle type="source" position={Position.Bottom} className="opacity-0" />
      )}
    </div>
  );
};

const HistoricalTimelineFlow = ({ order, statusHistory, onRefresh }) => {
  // Create timeline nodes from status history
  const { nodes, edges } = useMemo(() => {
    if (!order || !statusHistory || statusHistory.length === 0) {
      // Create a single node if no history is available
      const singleNode = {
        id: 'current',
        type: 'timeline',
        position: { x: 0, y: 0 },
        data: {
          label: 'Current Status',
          timestamp: order?.last_modified || order?.created_at || new Date().toISOString(),
          status: order?.status || 'Unknown',
          changes: ['Order is active'],
          isFirst: true,
          isLast: true,
          fulfillmentPercentage: order?.fulfillment_percentage || 0,
          icon: Package,
          changeType: 'status_change'
        }
      };
      
      return { nodes: [singleNode], edges: [] };
    }

    // Sort history by timestamp
    const sortedHistory = [...statusHistory].sort(
      (a, b) => new Date(a.created_at || a.timestamp) - new Date(b.created_at || b.timestamp)
    );

    // Create timeline nodes
    const timelineNodes = sortedHistory.map((historyItem, index) => {
      const isFirst = index === 0;
      const isLast = index === sortedHistory.length - 1;
      
      // Determine change type and icon
      let changeType = 'status_change';
      let icon = PlayCircle;
      
      if (historyItem.change_source === 'sync_new' || isFirst) {
        changeType = 'created';
        icon = Package;
      } else if (historyItem.new_fulfillment_percentage === 100 || 
                 (historyItem.new_status && historyItem.new_status.toLowerCase().includes('complete'))) {
        changeType = 'completed';
        icon = CheckCircle;
      } else if (historyItem.new_fulfillment_percentage !== historyItem.old_fulfillment_percentage) {
        changeType = 'fulfillment_update';
        icon = Truck;
      }

      // Build changes list
      const changes = [];
      if (historyItem.old_status && historyItem.new_status && historyItem.old_status !== historyItem.new_status) {
        changes.push(`Status: ${historyItem.old_status} → ${historyItem.new_status}`);
      }
      if (historyItem.old_fulfillment_percentage !== historyItem.new_fulfillment_percentage) {
        const oldPerc = historyItem.old_fulfillment_percentage || 0;
        const newPerc = historyItem.new_fulfillment_percentage || 0;
        changes.push(`Progress: ${oldPerc}% → ${newPerc}%`);
      }
      if (historyItem.old_finalised !== historyItem.new_finalised) {
        changes.push(`Finalized: ${historyItem.old_finalised ? 'Yes' : 'No'} → ${historyItem.new_finalised ? 'Yes' : 'No'}`);
      }
      if (historyItem.old_shipment_status !== historyItem.new_shipment_status && historyItem.new_shipment_status) {
        changes.push(`Shipment: ${historyItem.new_shipment_status}`);
      }

      // Fallback if no specific changes detected
      if (changes.length === 0) {
        if (historyItem.change_source === 'sync_new') {
          changes.push('Order created');
        } else {
          changes.push('Order updated');
        }
      }

      return {
        id: `timeline-${index}`,
        type: 'timeline',
        position: { x: 50, y: index * 180 },
        data: {
          label: isFirst ? 'Order Created' : (isLast ? 'Latest Update' : `Update ${index}`),
          timestamp: historyItem.created_at || historyItem.timestamp,
          status: historyItem.new_status || order?.status,
          changes,
          isFirst,
          isLast,
          fulfillmentPercentage: historyItem.new_fulfillment_percentage,
          icon,
          changeType
        }
      };
    });

    // Create edges between timeline nodes
    const timelineEdges = [];
    for (let i = 0; i < timelineNodes.length - 1; i++) {
      timelineEdges.push({
        id: `timeline-edge-${i}`,
        source: timelineNodes[i].id,
        target: timelineNodes[i + 1].id,
        type: 'smoothstep',
        animated: false,
        style: {
          stroke: '#fed402',
          strokeWidth: 2,
          strokeDasharray: '5,5'
        }
      });
    }

    return { nodes: timelineNodes, edges: timelineEdges };
  }, [order, statusHistory]);

  const [flowNodes, setNodes, onNodesChange] = useNodesState(nodes);
  const [flowEdges, setEdges, onEdgesChange] = useEdgesState(edges);

  // Custom node types
  const nodeTypes = useMemo(
    () => ({
      timeline: TimelineNode,
    }),
    []
  );

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  return (
    <div className="h-full w-full">
      <ReactFlowProvider>
        <ReactFlow
          nodes={flowNodes}
          edges={flowEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{
            padding: 0.2,
            includeHiddenNodes: false
          }}
          minZoom={0.3}
          maxZoom={1.2}
          defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#f1f5f9" gap={20} />
          <Controls showInteractive={false} />
          
          {/* Custom Panel with Timeline Info */}
          <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg p-4 z-10">
            <div className="flex items-center space-x-3 mb-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand text-brand-dark">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Order Timeline</h3>
                <p className="text-sm text-gray-600">{order?.order_number}</p>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Events:</span>
                <span className="font-medium">{statusHistory?.length || 1}</span>
              </div>
              
              {order?.created_at && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Created:</span>
                  <span className="font-medium">
                    {format(new Date(order.created_at), 'MMM dd, yyyy')}
                  </span>
                </div>
              )}
              
              {order?.last_modified && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Update:</span>
                  <span className="font-medium">
                    {format(new Date(order.last_modified), 'MMM dd, yyyy')}
                  </span>
                </div>
              )}
            </div>
            
            {/* Legend */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">Legend:</p>
              <div className="space-y-1 text-xs">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                  <span>Order Created</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-brand"></div>
                  <span>Status Change</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  <span>Fulfillment Update</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-green-600"></div>
                  <span>Completed</span>
                </div>
              </div>
            </div>
          </div>
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
};

export default HistoricalTimelineFlow;
